import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getUserMessages,
  createMessage,
  replyToMessage,
  subscribeToUserMessages,
  markMessageAsReadByUser,
} from '../utils/messageService';

function UserInbox() {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // New message form
  const [newSubject, setNewSubject] = useState('');
  const [newType, setNewType] = useState('question');
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Reply form
  const [replyContent, setReplyContent] = useState('');
  const [replying, setReplying] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        // Subscribe to real-time updates
        const unsubMessages = subscribeToUserMessages(user.uid, (messages) => {
          setMessages(messages);
          setLoading(false);
        });
        return () => unsubMessages();
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleCreateMessage = async () => {
    if (!newSubject || !newContent) {
      alert('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await createMessage(user.uid, user.email, newSubject, newContent, newType);
      setShowNewMessage(false);
      setNewSubject('');
      setNewType('question');
      setNewContent('');
      alert('✅ Message sent successfully!');
    } catch (error) {
      console.error('Error creating message:', error);
      alert('❌ Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async () => {
    if (!replyContent) {
      alert('Please enter a reply');
      return;
    }

    setReplying(true);
    try {
      await replyToMessage(selectedMessage.id, replyContent, 'user');
      setReplyContent('');
      alert('✅ Reply sent successfully!');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('❌ Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'help':
        return '🆘 Help';
      case 'question':
        return '❓ Question';
      case 'refund':
        return '💰 Refund';
      case 'credit':
        return '🎁 Credit';
      default:
        return type;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else if (timestamp.seconds) {
      // Firestore Timestamp object
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Just now';
    }
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography sx={{ color: 'text.primary' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '2px solid',
          borderColor: 'divider',
          py: 4,
          px: 3,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                📬 Inbox
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                Communicate with support team
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowNewMessage(true)}
              >
                New Message
              </Button>
              <Button onClick={() => navigate('/')} variant="outlined">
                ← Back
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '350px 1fr' }, gap: 3 }}>
          {/* Message List */}
          <Paper sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
              Messages ({messages.length})
            </Typography>
            {messages.length === 0 ? (
              <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                No messages yet
              </Typography>
            ) : (
              <List>
                {messages.map((msg) => (
                  <ListItem key={msg.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      selected={selectedMessage?.id === msg.id}
                      onClick={async () => {
                        setSelectedMessage(msg);
                        // Mark as read when user views the message
                        await markMessageAsReadByUser(msg.id);
                      }}
                      sx={{
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: selectedMessage?.id === msg.id ? 'primary.main' : 'divider',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(0,255,245,0.1)',
                          boxShadow: '0 0 10px rgba(0,255,245,0.2)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={msg.subject}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip label={getTypeLabel(msg.type)} size="small" sx={{ fontSize: '0.7rem' }} />
                          </Box>
                        }
                        primaryTypographyProps={{
                          sx: { color: 'text.primary', fontWeight: 600, fontSize: '0.9rem' },
                        }}
                        secondaryTypographyProps={{
                          component: 'div',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* Message Detail */}
          <Paper sx={{ p: 3 }}>
            {!selectedMessage ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Select a message to view conversation
                </Typography>
              </Box>
            ) : (
              <>
                {/* Message Header */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                        {selectedMessage.subject}
                      </Typography>
                      <Chip label={getTypeLabel(selectedMessage.type)} size="small" />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Messages Thread */}
                <Box sx={{ maxHeight: '40vh', overflow: 'auto', mb: 3 }}>
                  {selectedMessage.messages?.map((msg, index) => (
                    <Box
                      key={index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: msg.from === 'user' ? 'rgba(0,255,245,0.05)' : 'rgba(157,0,255,0.05)',
                        borderLeft: '3px solid',
                        borderColor: msg.from === 'user' ? 'primary.main' : 'secondary.main',
                        borderRadius: '8px',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.875rem' }}>
                          {msg.from === 'user' ? 'You' : 'Support Team'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                          {formatTimestamp(msg.timestamp)}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Reply Form */}
                {selectedMessage.status !== 'resolved' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <TextField
                        label="Your Reply"
                        multiline
                        rows={4}
                        fullWidth
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleReply}
                        disabled={replying || !replyContent}
                        fullWidth
                      >
                        {replying ? 'Sending...' : 'Send Reply'}
                      </Button>
                    </Box>
                  </>
                )}

                {selectedMessage.status === 'resolved' && (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography sx={{ color: 'success.main', fontWeight: 600 }}>
                      ✅ This conversation has been resolved
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Container>

      {/* New Message Dialog */}
      <Dialog
        open={showNewMessage}
        onClose={() => setShowNewMessage(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
          New Message
          <IconButton onClick={() => setShowNewMessage(false)} sx={{ ml: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Type</InputLabel>
            <Select value={newType} onChange={(e) => setNewType(e.target.value)} label="Type">
              <MenuItem value="question">❓ Question</MenuItem>
              <MenuItem value="help">🆘 Help</MenuItem>
              <MenuItem value="refund">💰 Refund Request</MenuItem>
              <MenuItem value="credit">🎁 Credit Request</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Subject"
            fullWidth
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Message"
            multiline
            rows={6}
            fullWidth
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowNewMessage(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCreateMessage} variant="contained" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserInbox;

